import { Component, OnInit, HostListener, Renderer2 } from '@angular/core';

import { AppService } from './services/app.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SiteContext } from './model';
import { HotelService } from './services/hotel.service';
import { AppContext } from './services/app-context.service';
import { Meta, Title } from '@angular/platform-browser';
import { filter, firstValueFrom } from 'rxjs';
import { AuthService } from './services/auth.service';
import { StorageService } from './services/storage.service';
import { LoginSignupService } from './services/login-signup.service';
import { ToasterService } from './services/toaster.service';
import { environment } from 'src/environments/environment';

declare const gtag: Function;

@Component({
  selector: 'travel-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  siteContext?: SiteContext;
  showLoader = false;
  loader = true;
  favIcon: HTMLLinkElement | null = document.querySelector('#appIcon');
  pageTitlePrefix: string = '';
  pageTitleSuffix: string = '';
  pageTitlePrefixShamal = 'Hotel Booking - Shamal Travels';
  pageTitlePrefixShamalB2B = 'B2B Hotel Booking Hub - Shamal Travels'

  @HostListener('document:keydown', ['$event'])
  handleKeyboardDownEvent(event: KeyboardEvent) {
    if (event.code === 'KeyH' && event.shiftKey && event.ctrlKey) {
      navigator.clipboard.writeText(this.hotelService.searchTracingKey);
    }
  }

  constructor(
    private appService: AppService,
    private meta: Meta,
    private titleService: Title,
    private hotelService: HotelService,
    private router: Router,
    private renderer: Renderer2,
    private activatedRoute: ActivatedRoute,
    private authSvc: AuthService,
    private appContext: AppContext,
    private loginsignupService: LoginSignupService,
    private storage: StorageService,
    private toasterSvc: ToasterService
  ) {
    this.createSession();

    const userDetails = JSON.parse(localStorage.getItem('user') as string);
    const logout_shopify_customer_id = localStorage.getItem(
      'logout_shopify_customer_id'
    );
    function getCookie(name: any) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) return parts.pop()?.split(';').shift();
    }
    const shopify_customer_id = getCookie('shopify_customer_id');

    if (userDetails) {
      this.storage.setUser(userDetails);
    } else if (
      (shopify_customer_id &&
        logout_shopify_customer_id &&
        logout_shopify_customer_id !== shopify_customer_id) ||
      (shopify_customer_id && !logout_shopify_customer_id)
    ) {
      this.getUserDetails(shopify_customer_id);
    }

    const currentUrl = this.router.url;


  }

  onActivate(e: any) {
    if (window) window.scroll(0, 0);
  }

  createSession() {
    this.loader = true; // Show loader while fetching session data

    this.appContext.createSession().subscribe({
      next: (res: SiteContext) => {
        this.siteContext = res;
        sessionStorage.setItem('SiteContext', JSON.stringify(this.siteContext));

        this.appService.sessionKey = res.sessionKey;
        this.changeTheme();
        this.configureSite();

        // Load scripts based on display settings
        this.loadTrackingScripts();
        this.loadGoogleMaps();
        this.loadSiteSpecificScripts();

        this.loader = false; // Hide loader after processing
      },
      error: (err) => {
        console.error('Error creating session:', err);
        this.loader = false; // Ensure loader is hidden on error
      }
    });
  }

  /** Loads tracking scripts like Google Analytics & GTM */
  private loadTrackingScripts() {
    const settings = this.siteContext?.displaySetting;
    if (!settings) return;

    if (settings.googleTagManagerScript && settings.googleAnalyticsCode) {
      this.loadScript(`https://www.googletagmanager.com/gtm.js?id=${settings.googleAnalyticsCode}`);
      this.loadScript(`https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsCode}`);
      this.loadInlineScript(settings.googleTagManagerScript);
      this.loadInlineScript(`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${settings.googleAnalyticsCode}');`);
    } else if (settings.googleAnalyticsCode) {
      this.loadScript(`https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsCode}`);
      this.loadInlineScript(`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${settings.googleAnalyticsCode}');`);
    }
  }

  /** Loads Google Maps if key is available */
  private loadGoogleMaps() {
    if (this.siteContext?.displaySetting?.googleMapKey) {
      this.loadScript(`https://maps.googleapis.com/maps/api/js?key=${this.siteContext.displaySetting.googleMapKey}&libraries=places`);
    }
  }

  /** Loads site-specific scripts */
  private loadSiteSpecificScripts() {
    const siteName = this.appContext.siteName;

    if (siteName === 'ShamalGroup') {
      this.loadScript(environment.peachPamentScript);
    }

    if (siteName === 'Gocaribbea') {
      this.loadZohoSalesIQ();
      this.loadClarityScript();
    }

    if (this.siteContext?.name === 'Joy') {
      this.loadInlineScript(`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','GTM-M9K6L9LC');`);
    }
  }

  /** Loads Zoho SalesIQ for Gocaribbea */
  private loadZohoSalesIQ() {
    this.loadInlineScript(`window.$zoho=window.$zoho || {};$zoho.salesiq=$zoho.salesiq||{ready:function(){}}`);
    this.loadScript('https://salesiq.zohopublic.in/widget?wc=siqabc9e41f92cf4914028b3fb28b1f394166a098ad69640b2c169bad694e5f37b8');
  }

  /** Loads Microsoft Clarity tracking script */
  private loadClarityScript() {
    this.loadInlineScript(`(function(c,l,a,r,i,t,y){ c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}; t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i; y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y); })(window, document, "clarity", "script", "obd8u5u1v5");`);
  }

  /** Utility method to load external scripts */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve(); // Prevent duplicate loading

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to >>>>>>>>>>>> uutrdgctfcxft load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  private async loadInlineScript(code: string) {
    // console.log("code=>", code);

    // Use regex to extract script content
    const scriptMatch = code.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
    if (scriptMatch) {
      code = scriptMatch[1].trim(); // Extract and clean script content
    }

    // Prevent duplicate script insertion
    const existingScripts = Array.from(document.scripts).map(script => script.textContent);
    if (existingScripts.includes(code)) {
      console.warn("Script already exists, skipping...");
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.textContent = code; // Use textContent to avoid HTML parsing issues
    document.head?.appendChild(script);
  }



  ngOnInit() {
    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        const rt = this.getChild(this.activatedRoute);
        rt.data.subscribe((data: any) => {
          setTimeout(() => {
            if (this.siteContext?.name == 'FTD' && data.title == 'Home') {
              let title =
                this.siteContext.displaySetting.metaTagas[0]?.content || '';
              this.titleService.setTitle(this.pageTitlePrefix + ' ' + title);
            } else {
              this.pageTitleSuffix = data.title || '';
              if (data.title && this.siteContext?.name != 'ShamalGroup')
                this.titleService.setTitle(
                  data.title + ' | ' + this.pageTitlePrefix
                );
              if (this.siteContext?.name == 'ShamalGroup') {
                if (
                  this.siteContext.displaySetting.pageTitlePrefix ==
                  'ShamalGroup'
                )
                  this.titleService.setTitle(this.pageTitlePrefixShamal);
                if (
                  this.siteContext.displaySetting.pageTitlePrefix ==
                  'ShamalGroup-B2B'
                )
                  this.titleService.setTitle(
                    this.pageTitlePrefixShamalB2B
                  );
              }
            }
          }, 1000);
        });
      });
  }

  getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  changeTheme() {
    if (this.siteContext?.displaySetting?.styles) {
      var siteStyel = this.siteContext.displaySetting?.styles;
      if (siteStyel.cssVariables?.length > 0) {
        siteStyel.cssVariables.forEach((itm: any) => {
          document.documentElement.style.setProperty(itm.name, itm.value);
        });
      }
    }
  }

  configureSite() {
    if (!this.siteContext?.displaySetting) return;
    const displaySetting = this.siteContext.displaySetting;

    if (displaySetting.metaTagas.length > 0) {
      this.meta.addTags(displaySetting.metaTagas);
    }

    this.pageTitlePrefix = displaySetting.pageTitlePrefix;

    if (this.siteContext?.name == 'ShamalGroup') {
      if (this.siteContext.displaySetting.pageTitlePrefix == 'ShamalGroup')
        this.titleService.setTitle(this.pageTitlePrefixShamal);
      if (this.siteContext.displaySetting.pageTitlePrefix == 'ShamalGroup-B2B')
        this.titleService.setTitle(this.pageTitlePrefixShamalB2B);
    } else {
      this.titleService.setTitle(
        this.pageTitleSuffix + ' | ' + displaySetting.pageTitlePrefix
      );
    }

    if (this.favIcon && displaySetting.favIcon)
      this.favIcon.href = displaySetting.favIcon;
  }


  async getUserDetails(id: string) {
    try {
      const user = await firstValueFrom(
        this.loginsignupService.getUserByID(id)
      );
      this.storage.setUser(user);
      this.toasterSvc.success('Login is successful.', 5000);
    } catch (error: any) {
      this.toasterSvc.error('Something went wrong!');
    }
  }
}
